import {
  createAppSelector,
  selectClassSessions,
  selectCurrentCourseId,
  selectCurrentUserId,
} from "./base";

import type { ClassSessionType } from "components/server-types";
import { ClassSessionCategory } from "components/server-types";
import { RootState } from "store";
import { getActiveClassSession, isClassSessionActive } from "utils/class-session";

export const selectClassSessionByCourseId = createAppSelector(
  [selectClassSessions, (state, courseId: string | null) => courseId],
  (classSessions, courseId) => getActiveClassSession(classSessions, courseId),
);

export const selectActiveClassSession = createAppSelector(
  [selectClassSessions, selectCurrentCourseId],
  (classSessions, courseId) => getActiveClassSession(classSessions, courseId),
);

export const selectActiveClassSessionId: (state: RootState) => string | null = createAppSelector(
  [selectActiveClassSession],
  (classSession: ClassSessionType | null) => classSession?.id || null,
);

export const selectActiveClassSessionCategory = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category || null,
);

export const selectIsClassSessionInPerson = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category === ClassSessionCategory.IN_PERSON,
);

export const selectIsClassSessionVirtual = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.category === ClassSessionCategory.VIRTUAL,
);

export const selectIsPracticeClassSession = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.is_practice || false,
);

// Separate from selectActiveClassSession just to be more specific and prevent re-renders
export const selectIsPresentationActive = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => Boolean(activeSession?.presented_page_id),
);

export const selectPresentedPageId = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => activeSession?.presented_page_id || null,
);

export const selectPresentingUserId = createAppSelector(
  [selectActiveClassSession],
  (activeSession) => activeSession?.presenting_user_id || null,
);

export const selectPresentingSessions = createAppSelector(
  [selectClassSessions, selectCurrentUserId],
  (classSessions, userId) =>
    classSessions.filter((cs) => cs.presenting_user_id === userId && isClassSessionActive(cs)),
);

export const selectAllVisibleClassSessionIdsForCurrentCourse: (state: RootState) => string[] =
  createAppSelector([selectClassSessions, selectCurrentCourseId], (classSessions, courseId) =>
    classSessions
      .filter((cs) => cs.course_id === courseId && !cs.is_hidden_from_session_logs)
      .map((cs) => cs.id),
  );
